import { ReactElement, createContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import CompanyManager from '../services/api/CompanyManager';
import useAuth from '../hooks/global/useAuth';
import useStore from '../hooks/store/useStore';
import { COUPON_TYPES, PROMO_CODE_TYPES, REDUX_STORE_SLICES } from '../services/exports/Constants';
import { useTranslation } from 'react-i18next';
import { ReservedHttpResponseCodes } from '../services/exports/Types';
import MultiPromoCodeModal from '../modals/MultiPromoCodeModal';
import useMobileApp from '../hooks/mobile-app/useMobileApp';
import { modalRouterRef } from '../modules/mobile/router/ModalRouter';
import { Modals } from '../modules/mobile/exports/Enums';
import { actionCreators } from '../store/actions';
import ReduxActions from '../store/ReduxActions';
import discountDisplacementSlice from '../store/slices/DiscountDisplacementSlice';
import useAlert from '../hooks/utility/useAlert';
import { CouponDetailsInterface } from '../services/exports/Interfaces';
import useAnalytics from '../hooks/analytics/useAnalytics';
import useGetPromoCodesApi from '../hooks/api/companies/useGetPromoCodesApi';
import { masterPromoEventProps } from '../utils/analytics/dealEventProps';
import { DEDUPE_NAMESPACES, markOnce } from '../utils/analytics/sessionDedupe';
import { resolvePromoCodeRefresh } from '../utils/promo-code-refresh';

interface Props {
  children: ReactElement;
}

interface IPromoCodeContext {
  promoCodeInput: string;
  appliedPromoCode: string | null;
  showLoginPrompt: boolean;
  isLoading: boolean;
  setPromoCode: (promoCode: CouponDetailsInterface, _isLoggedIn?: boolean) => void;
  setPromoCodeInput: (promoCodeInput: string) => void;
  removePromoCode: () => void;
  applyPromoCode: (code?: string, _isLoggedIn?: boolean) => Promise<any>;
  openMultiPromoModal: (promoCode: CouponDetailsInterface) => void;
}

export const PromoCodeContext = createContext<IPromoCodeContext>(null);

const PromoCodeProvider = ({ children }: Props) => {
  const { isLoggedIn } = useAuth();
  const { isMobileApp } = useMobileApp();
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const { cached_order } = useStore<REDUX_STORE_SLICES.ORDER>(REDUX_STORE_SLICES.ORDER);
  const { profile } = useStore<REDUX_STORE_SLICES.PROFILE>(REDUX_STORE_SLICES.PROFILE);
  const { t } = useTranslation(undefined, {
    keyPrefix: 'Providers:PromoCodeProvider',
  });
  const alert = useAlert();
  const { trackOfferApplied } = useAnalytics();
  const { data: promoCodes } = useGetPromoCodesApi();
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [promoCodeInput, setPromoCodeInput] = useState<string>(cached_order?.coupon_details?.code ?? '');
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(cached_order?.coupon_details?.code);
  const [showMultiPromoCodeModal, setShowMultiPromoCodeModal] = useState(false);
  // The multi-promo whose detail modal is open for PREVIEW (opened from a card, not yet applied).
  const [previewPromoCode, setPreviewPromoCode] = useState<CouponDetailsInterface | null>(null);

  useEffect(() => {
    if (cached_order?.coupon_type !== COUPON_TYPES.PROMO_CODE && appliedPromoCode) {
      setPromoCodeInput('');
      setAppliedPromoCode(null);
    }
  }, [cached_order?.coupon_type, cached_order?.coupon_details?.code]);

  const actualizePromoCode = async (): Promise<void> => {
    setIsLoading(true);
    const { response, success } = await CompanyManager.getPromoCodeDetails(
      company?.id,
      cached_order.coupon_details.code,
    );
    setIsLoading(false);

    if (!success) {
      removePromoCode();

      return;
    }

    const actualPromoCode = response.data.data as CouponDetailsInterface;
    const refresh = resolvePromoCodeRefresh(cached_order.coupon_details, actualPromoCode);

    if (refresh === 'remove') {
      removePromoCode();
    } else if (refresh === 'replace') {
      setPromoCode(actualPromoCode);
    }
  };

  useEffect(() => {
    if (cached_order?.coupon_details?.code && profile?.id) {
      actualizePromoCode();
    }
  }, []);

  const applyPromoCode = async (code?: string, _isLoggedIn?: boolean): Promise<any> => {
    if (!(_isLoggedIn ?? isLoggedIn)) {
      return setShowLoginPrompt(true);
    }

    setIsLoading(true);
    const { response, success } = await CompanyManager.getPromoCodeDetails(company?.id, code ?? promoCodeInput);
    setIsLoading(false);

    if (!success) {
      applyPromoErrorHandler(response);

      return;
    }

    setPromoCode(response.data.data as CouponDetailsInterface, _isLoggedIn);
    toast.success(t('feedback.promo_code_added'));

    const appliedCode = (response.data.data as CouponDetailsInterface)?.code ?? code ?? promoCodeInput;
    const matchedPromo = (promoCodes ?? []).find((promo) => promo.code === appliedCode);
    const analytics = masterPromoEventProps(
      { code: appliedCode, image_url: matchedPromo?.image_url },
      { restaurantId: company?.id, isLoggedIn: _isLoggedIn ?? isLoggedIn },
    );

    if (markOnce(DEDUPE_NAMESPACES.Applied, analytics.offer_id)) {
      trackOfferApplied(analytics);
    }

    return true;
  };

  function applyPromoErrorHandler(error: any): void {
    if (error?.response?.status === ReservedHttpResponseCodes.ErrorResponseAlertWindow) {
      alert.error({
        title: error?.response?.data?.title,
        description: error?.response?.data?.description,
      });
    }

    removePromoCode();
  }

  const removePromoCode = (): void => {
    setPromoCodeInput('');

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        coupon_type: null,
        coupon_details: null,
      }),
    );

    appliedPromoCode && toast.error(t('feedback.promo_code_removed', { code: appliedPromoCode }));

    setAppliedPromoCode(null);
  };

  const setPromoCode = (promoCode: CouponDetailsInterface, _isLoggedIn?: boolean): void => {
    if (!(_isLoggedIn ?? isLoggedIn)) {
      return setShowLoginPrompt(true);
    }

    // A promo code is exclusive with everything: applying it here overwrites coupon_type, which
    // clears an active points redemption. Record what it displaced so the notice can explain it
    // and Undo can restore the points (FOO-2555 / PROD-407).
    const displacedPoints =
      cached_order?.coupon_type === COUPON_TYPES.CASHBACK ? (cached_order?.cashback_points ?? 0) : 0;
    if (displacedPoints > 0) {
      ReduxActions.dispatch(
        discountDisplacementSlice.actions.setCouponDisplacedPoints({
          kind: 'promo',
          label: promoCode.code,
          points: displacedPoints,
        }),
      );
    }

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        coupon_type: COUPON_TYPES.PROMO_CODE,
        coupon_details: promoCode,
      }),
    );

    setPromoCodeInput(promoCode.code);
    setAppliedPromoCode(promoCode.code);

    // Auto-open the breakdown only for a code applied outside the preview modal (e.g. typed in).
    // When the customer applies from the preview modal it is already open — leave it so the footer
    // just flips from "Apply" to "Applied · Remove".
    if (promoCode.type === PROMO_CODE_TYPES.MULTI_PROMO_CODE && !isMultiPromoModalOpen()) {
      presentMultiPromoModal();
    }
  };

  // In the mobile app the breakdown is a native Ion sheet driven by the modal router (FOO-3200);
  // on web it's the CustomModal gated by `showMultiPromoCodeModal`. Read the right "is it open"
  // signal per surface so we don't stack a second sheet when a code is (re)applied while open.
  const isMultiPromoModalOpen = (): boolean =>
    isMobileApp
      ? modalRouterRef.current?.isOpen === true && modalRouterRef.current?.config?.id === Modals.MultiPromoCode
      : showMultiPromoCodeModal;

  const presentMultiPromoModal = (previewCode?: CouponDetailsInterface | null): void => {
    if (isMobileApp) {
      modalRouterRef.current?.openModal({
        id: Modals.MultiPromoCode,
        props: previewCode ? { previewPromoCode: previewCode } : undefined,
      });

      return;
    }

    setPreviewPromoCode(previewCode ?? null);
    setShowMultiPromoCodeModal(true);
  };

  // Open the multi-promo detail modal in PREVIEW — show the tiers + an Apply button, without
  // applying the code yet (prototype flow: the customer must click Apply).
  const openMultiPromoModal = (promoCode: CouponDetailsInterface): void => presentMultiPromoModal(promoCode);

  const closeMultiPromoCodeModal = (): void => {
    setShowMultiPromoCodeModal(false);
    setPreviewPromoCode(null);
  };

  // On the app the modal is mounted on demand by the Ion `ModalRouter`; only the web surface
  // renders it here through the CustomModal (FOO-3200).
  const renderMultiPromoCodeModal = isMobileApp ? null : (
    <MultiPromoCodeModal
      openModal={showMultiPromoCodeModal}
      toggleModal={closeMultiPromoCodeModal}
      previewPromoCode={previewPromoCode}
    />
  );

  const context = useMemo(
    () => ({
      promoCodeInput,
      appliedPromoCode,
      showLoginPrompt,
      isLoading,
      setPromoCode,
      setPromoCodeInput,
      removePromoCode,
      applyPromoCode,
      openMultiPromoModal,
    }),
    [promoCodeInput, appliedPromoCode, showLoginPrompt, isLoading, isLoggedIn],
  );

  return (
    <PromoCodeContext.Provider value={context}>
      {children}
      {renderMultiPromoCodeModal}
    </PromoCodeContext.Provider>
  );
};

export default PromoCodeProvider;
